import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import React, { ReactNode, forwardRef, useState } from "react";
import { Collapse, Switch } from "@material-ui/core";
import { styled } from "@material-ui/styles";

interface CollapsiblePanelProps {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
  required?: boolean;
  collapsible?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  /** Template mode: show an include/exclude toggle switch in the header */
  includeToggle?: boolean;
  /** Controlled included state — when false the panel is collapsed */
  included?: boolean;
  onIncludedChange?: (included: boolean) => void;
  hasError?: boolean;
}

const PanelRoot = styled("div")(({ theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  padding: 0,
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "border-color 0.2s ease",
  "&.panel-error": {
    border: `2px solid ${theme.palette.brand.errorRed}`,
  },
}));

const PanelHeaderButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: 16,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
}));

const PanelHeaderStatic = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 16,
});

const PanelTitle = styled("span")(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const RequiredBadge = styled("span")(({ theme }) => ({
  background: "#FFF1EE",
  color: theme.palette.brand.coral,
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 500,
  padding: "2px 8px",
  marginLeft: 8,
}));

const PanelDivider = styled("div")(({ theme }) => ({
  height: 1,
  background: theme.palette.brand.gray200,
}));

const PanelBody = styled("div")({
  padding: 16,
  flex: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

const CollapsiblePanel = forwardRef<HTMLDivElement, CollapsiblePanelProps>(
  (
    {
      title,
      children,
      defaultExpanded = false,
      required = false,
      collapsible = true,
      isOpen: isOpenProp,
      onToggle,
      includeToggle = false,
      included = true,
      onIncludedChange,
      hasError = false,
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(defaultExpanded);
    const isControlled = isOpenProp !== undefined;
    const open = isControlled ? isOpenProp : internalOpen;

    const handleToggle = () => {
      if (isControlled) {
        onToggle?.();
      } else {
        setInternalOpen((v) => !v);
      }
    };

    const handleIncludeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onIncludedChange?.(e.target.checked);
    };

    const body = (
      <>
        <PanelDivider />
        <PanelBody>{children}</PanelBody>
      </>
    );

    if (!collapsible) {
      return (
        <PanelRoot ref={ref} className={hasError ? "panel-error" : undefined}>
          <PanelHeaderStatic>
            <PanelTitle>{title}</PanelTitle>
            {required && <RequiredBadge>Required</RequiredBadge>}
          </PanelHeaderStatic>
          {body}
        </PanelRoot>
      );
    }

    return (
      <PanelRoot ref={ref} className={hasError ? "panel-error" : undefined}>
        <PanelHeaderButton
          onClick={includeToggle && !included ? undefined : handleToggle}
          style={{ cursor: includeToggle && !included ? "default" : "pointer" }}
        >
          <PanelTitle style={{ opacity: includeToggle && !included ? 0.45 : 1 }}>{title}</PanelTitle>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {required && <RequiredBadge>Required</RequiredBadge>}
            {includeToggle && (
              <Switch
                size="small"
                checked={included}
                onChange={handleIncludeChange}
                onClick={(e) => e.stopPropagation()}
                color="primary"
              />
            )}
            {!includeToggle && (
              <ExpandMoreIcon
                style={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            )}
          </span>
        </PanelHeaderButton>
        <Collapse in={includeToggle ? included : open}>{body}</Collapse>
      </PanelRoot>
    );
  },
);

CollapsiblePanel.displayName = "CollapsiblePanel";

export default CollapsiblePanel;
